import { FC, useCallback, useMemo } from 'react';
import { IconButton, Tooltip } from 'components';
import { useTranslation } from 'react-i18next';
import { SortDescend, SortReverseDescend } from 'components/icons';
import { useSort } from 'providers';

interface SortProps {
    resourceId: string;
    columnId: string;
}

const Sort: FC<SortProps> = ({ resourceId, columnId }) => {
    const { t } = useTranslation();
    const { setSort, state } = useSort();
    const isSorted = useMemo(
        () => state?.[resourceId]?.sortingField === columnId,
        [state, resourceId, columnId]
    );
    const isSortedDesc = useMemo(
        () =>
            state?.[resourceId]?.sortingField === columnId &&
            state?.[resourceId]?.order === 'Descending',
        [state, resourceId, columnId]
    );

    const sort = useCallback(() => {
        setSort(
            resourceId,
            isSorted
                ? isSortedDesc
                    ? null
                    : {
                          sortingField: columnId,
                          order: 'Descending'
                      }
                : {
                      sortingField: columnId,
                      order: 'Ascending'
                  }
        );
    }, [setSort, resourceId, isSorted, isSortedDesc, columnId]);

    return (
        <Tooltip
            title={t(
                `expressions.sort_after_${
                    isSorted
                        ? isSortedDesc
                            ? 'Descending'
                            : 'Ascending'
                        : 'None'
                }`
            )}>
            <IconButton
                size={'small'}
                icon={
                    isSortedDesc ? (
                        <SortDescend size={16} color={'primary'} />
                    ) : (
                        <SortReverseDescend size={16} color={'primary'} />
                    )
                }
                isActive={isSorted}
                onClick={sort}
            />
        </Tooltip>
    );
};

export default Sort;
